import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import {
  Cctv, Loader2, RefreshCw, Sparkles, Plus, Trash2, Settings, Search,
  Car, Users, AlertTriangle, ImageOff, Wifi, Camera, ScanLine, Maximize2,
  Download, KeyRound, UserSearch, MonitorSmartphone
} from "lucide-react";

const fmtFecha = (s) => (s ? new Date(s).toLocaleString("es-DO", { dateStyle: "short", timeStyle: "short" }) : "-");

const CamaraTile = ({ nvr, canal, getAuthHeaders, onAnalizar, onAmpliar, onAuthError }) => {
  const [imgUrl, setImgUrl] = useState(null);
  const [error, setError] = useState(false);
  const [sesionExpirada, setSesionExpirada] = useState(false);
  const [cargando, setCargando] = useState(true);
  const urlRef = useRef(null);

  const cargar = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/camaras/snapshot/${nvr.id}/${canal.numero}`, {
        ...getAuthHeaders(), responseType: "blob"
      });
      const url = URL.createObjectURL(res.data);
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = url;
      setImgUrl(url);
      setError(false);
      setSesionExpirada(false);
    } catch (err) {
      if (err?.response?.status === 401) {
        setSesionExpirada(true);
        if (onAuthError) onAuthError();
      } else {
        setError(true);
      }
    } finally {
      setCargando(false);
    }
  }, [nvr.id, canal.numero, getAuthHeaders, onAuthError]);

  useEffect(() => {
    cargar();
    const t = setInterval(cargar, 10000);
    return () => {
      clearInterval(t);
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    };
  }, [cargar]);

  return (
    <Card className="overflow-hidden" data-testid={`camara-tile-${nvr.id}-${canal.numero}`}>
      <div className="relative aspect-video bg-slate-900 flex items-center justify-center cursor-pointer group" onClick={() => imgUrl && onAmpliar(nvr, canal)}>
        {cargando && <Loader2 className="w-6 h-6 animate-spin text-slate-500" />}
        {!cargando && sesionExpirada && (
          <div className="text-center text-amber-400 px-2" data-testid={`camara-sesion-expirada-${nvr.id}-${canal.numero}`}>
            <KeyRound className="w-8 h-8 mx-auto mb-1" />
            <p className="text-xs">Sesión expirada — la cámara sí está conectada</p>
          </div>
        )}
        {!cargando && error && !sesionExpirada && (
          <div className="text-center text-slate-500">
            <ImageOff className="w-8 h-8 mx-auto mb-1" />
            <p className="text-xs">Sin conexión</p>
          </div>
        )}
        {imgUrl && !error && !sesionExpirada && (
          <>
            <img src={imgUrl} alt={canal.nombre} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
              <Maximize2 className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </>
        )}
        {canal.es_entrada && <Badge className="absolute top-2 left-2 bg-amber-500 text-xs"><ScanLine className="w-3 h-3 mr-1" />Matrículas</Badge>}
      </div>
      <CardContent className="p-2 flex items-center justify-between gap-1">
        <div className="min-w-0">
          <p className="text-sm font-medium truncate dark:text-white">{canal.nombre}</p>
          <p className="text-xs text-slate-400 truncate">{nvr.nombre} · Ch {canal.numero}</p>
        </div>
        <div className="flex shrink-0 gap-1">
          <Button variant="ghost" size="sm" className="px-2" onClick={cargar} title="Refrescar" data-testid={`camara-refrescar-${nvr.id}-${canal.numero}`}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" className="px-2 text-violet-600 border-violet-200" onClick={() => onAnalizar(nvr, canal)} title="Analizar con IA" data-testid={`camara-analizar-${nvr.id}-${canal.numero}`}>
            <Sparkles className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const PersonaFoto = ({ personaId, nombre, getAuthHeaders }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let obj = null;
    axios.get(`${API}/camaras/personas/${personaId}/foto`, { ...getAuthHeaders(), responseType: "blob" })
      .then((res) => { obj = URL.createObjectURL(res.data); setUrl(obj); })
      .catch(() => {});
    return () => { if (obj) URL.revokeObjectURL(obj); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personaId]);
  if (!url) return <div className="w-20 h-20 rounded-full mx-auto mb-1 bg-slate-200 dark:bg-slate-700 flex items-center justify-center"><Users className="w-8 h-8 text-slate-400" /></div>;
  return <img src={url} alt={nombre} className="w-20 h-20 object-cover rounded-full mx-auto mb-1" />;
};

const NVR_VACIO = { id: null, nombre: "", host: "", puerto: 80, usuario: "", password: "", modo: "agente", canales: [] };

export default function CamarasPage() {
  const { getAuthHeaders, user, logout } = useAuth();
  const isAdmin = ["admin", "administrador"].includes((user?.role || "").toLowerCase());

  const [nvrs, setNvrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sesionExpirada, setSesionExpirada] = useState(false);
  const handleAuthError = useCallback(() => setSesionExpirada(true), []);

  // Análisis IA
  const [analisis, setAnalisis] = useState(null); // {camara, nvr, canal, cargando, resultado, imagen_b64}
  const [preguntaIA, setPreguntaIA] = useState("");
  // Ampliar
  const [ampliada, setAmpliada] = useState(null); // {nvr, canal, url}
  const ampliadaUrlRef = useRef(null);

  // Matrículas
  const [matriculas, setMatriculas] = useState([]);
  const [matCfg, setMatCfg] = useState({ activo: false, intervalo_min: 2, hora_inicio: "", hora_fin: "" });
  const [buscarMat, setBuscarMat] = useState("");
  const [fotoMat, setFotoMat] = useState(null);
  const [recortes, setRecortes] = useState({});
  const [guardandoCfg, setGuardandoCfg] = useState(false);

  // Config NVR
  const [showNvrDialog, setShowNvrDialog] = useState(false);
  const [nvrForm, setNvrForm] = useState(NVR_VACIO);
  const [guardandoNvr, setGuardandoNvr] = useState(false);
  const [probando, setProbando] = useState(false);

  // Agente local
  const [agenteCfg, setAgenteCfg] = useState(null);
  const [mostrarKey, setMostrarKey] = useState(false);

  // Detecciones de personas
  const [detecciones, setDetecciones] = useState([]);
  const [detCfg, setDetCfg] = useState({ activo: false, intervalo_min: 2, hora_inicio: "", hora_fin: "" });
  const [guardandoDetCfg, setGuardandoDetCfg] = useState(false);
  const [personas, setPersonas] = useState([]);
  const [showPersonaDialog, setShowPersonaDialog] = useState(false);
  const [personaForm, setPersonaForm] = useState({ nombre: "", notas: "", foto: null });
  const [guardandoPersona, setGuardandoPersona] = useState(false);
  const [fotoDet, setFotoDet] = useState(null);

  const fetchNvrs = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/camaras/nvr`, getAuthHeaders());
      setNvrs(res.data || []);
    } catch {
      toast.error("Error al cargar los NVR");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  const fetchMatriculas = useCallback(async (q = "") => {
    try {
      const res = await axios.get(`${API}/camaras/matriculas`, { ...getAuthHeaders(), params: q ? { buscar: q } : {} });
      setMatriculas(res.data || []);
    } catch { /* silencioso */ }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchNvrs();
    fetchMatriculas();
    axios.get(`${API}/camaras/matriculas/config`, getAuthHeaders()).then((r) => setMatCfg(r.data)).catch(() => {});
    axios.get(`${API}/camaras/detecciones/config`, getAuthHeaders()).then((r) => setDetCfg(r.data)).catch(() => {});
    axios.get(`${API}/camaras/detecciones`, getAuthHeaders()).then((r) => setDetecciones(r.data || [])).catch(() => {});
    axios.get(`${API}/camaras/personas`, getAuthHeaders()).then((r) => setPersonas(r.data || [])).catch(() => {});
    if (isAdmin) axios.get(`${API}/camaras/agente/config`, getAuthHeaders()).then((r) => setAgenteCfg(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchMatriculas(buscarMat), 400);
    return () => clearTimeout(t);
  }, [buscarMat, fetchMatriculas]);

  const camarasActivas = nvrs.flatMap((nvr) => (nvr.canales || []).filter((c) => c.activo !== false).map((c) => ({ nvr, canal: c })));

  const handleAnalizar = async (nvr, canal, pregunta = null) => {
    setAnalisis((prev) => ({ camara: `${canal.nombre} (${nvr.nombre})`, nvr, canal, cargando: true, preguntaEnviada: pregunta }));
    try {
      const res = await axios.post(`${API}/camaras/analizar`, { nvr_id: nvr.id, canal: canal.numero, ...(pregunta ? { pregunta } : {}) }, getAuthHeaders());
      setAnalisis({ camara: `${canal.nombre} (${nvr.nombre})`, nvr, canal, cargando: false, preguntaEnviada: pregunta, ...res.data });
    } catch (err) {
      setAnalisis(null);
      toast.error(err.response?.data?.detail || "No se pudo analizar la cámara");
    }
  };

  const handleAmpliar = async (nvr, canal) => {
    setAmpliada({ nvr, canal, url: null });
    try {
      const res = await axios.get(`${API}/camaras/snapshot/${nvr.id}/${canal.numero}`, { ...getAuthHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (ampliadaUrlRef.current) URL.revokeObjectURL(ampliadaUrlRef.current);
      ampliadaUrlRef.current = url;
      setAmpliada({ nvr, canal, url });
    } catch {
      toast.error("No se pudo cargar la imagen");
      setAmpliada(null);
    }
  };

  const handleVerFoto = async (registro) => {
    if (!registro.captura_id) return;
    setFotoMat({ registro, url: null, recorteUrl: recortes[registro.id] || null });
    try {
      const res = await axios.get(`${API}/camaras/capturas/${registro.captura_id}`, { ...getAuthHeaders(), responseType: "blob" });
      setFotoMat((prev) => prev ? { ...prev, url: URL.createObjectURL(res.data) } : prev);
    } catch {
      toast.error("No se pudo cargar la captura");
      setFotoMat(null);
    }
  };

  useEffect(() => {
    let cancelado = false;
    const cargar = async () => {
      const pendientes = matriculas.filter((m) => m.recorte_id && !recortes[m.id]).slice(0, 100);
      if (!pendientes.length) return;
      const nuevos = {};
      await Promise.all(pendientes.map(async (m) => {
        try {
          const res = await axios.get(`${API}/camaras/capturas/${m.recorte_id}`, { ...getAuthHeaders(), responseType: "blob" });
          nuevos[m.id] = URL.createObjectURL(res.data);
        } catch { /* sin recorte */ }
      }));
      if (!cancelado && Object.keys(nuevos).length) setRecortes((prev) => ({ ...prev, ...nuevos }));
    };
    cargar();
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matriculas]);

  const guardarMatCfg = async () => {
    setGuardandoCfg(true);
    try {
      await axios.put(`${API}/camaras/matriculas/config`, {
        ...matCfg, intervalo_min: parseInt(matCfg.intervalo_min, 10) || 2
      }, getAuthHeaders());
      toast.success("Configuración de matrículas guardada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setGuardandoCfg(false);
    }
  };

  const guardarNvr = async () => {
    if (!nvrForm.nombre || !nvrForm.host || !nvrForm.usuario) {
      toast.error("Complete nombre, host y usuario");
      return;
    }
    setGuardandoNvr(true);
    try {
      await axios.post(`${API}/camaras/nvr`, {
        ...nvrForm,
        puerto: parseInt(nvrForm.puerto, 10) || 80,
        canales: nvrForm.canales.map((c) => ({ ...c, numero: parseInt(c.numero, 10) || 1 }))
      }, getAuthHeaders());
      toast.success("NVR guardado");
      setShowNvrDialog(false);
      fetchNvrs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar el NVR");
    } finally {
      setGuardandoNvr(false);
    }
  };

  const probarNvr = async () => {
    if (!nvrForm.id) {
      toast.info("Guarde el NVR primero para probar la conexión");
      return;
    }
    setProbando(true);
    try {
      const res = await axios.post(`${API}/camaras/nvr/${nvrForm.id}/test`, {}, getAuthHeaders());
      if (res.data.success) toast.success(res.data.mensaje);
      else toast.error(res.data.mensaje);
    } catch {
      toast.error("Error al probar la conexión");
    } finally {
      setProbando(false);
    }
  };

  const eliminarNvr = async (nvr) => {
    if (!window.confirm(`¿Eliminar el NVR "${nvr.nombre}" y sus cámaras?`)) return;
    try {
      await axios.delete(`${API}/camaras/nvr/${nvr.id}`, getAuthHeaders());
      toast.success("NVR eliminado");
      fetchNvrs();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const setCanal = (idx, cambios) => {
    setNvrForm((f) => ({ ...f, canales: f.canales.map((c, i) => (i === idx ? { ...c, ...cambios } : c)) }));
  };

  const descargarAgente = async () => {
    try {
      const res = await axios.get(`${API}/camaras/sync/agent-package`, { ...getAuthHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "QAsesoresCamarasAgente.zip";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Error al descargar el agente");
    }
  };

  const regenerarKey = async () => {
    if (!window.confirm("¿Regenerar la llave API? Deberá reinstalar el agente con el nuevo paquete.")) return;
    try {
      const res = await axios.post(`${API}/camaras/agente/regenerate-key`, {}, getAuthHeaders());
      setAgenteCfg((c) => ({ ...c, api_key: res.data.api_key }));
      toast.success("Llave regenerada. Descargue e instale el agente de nuevo.");
    } catch {
      toast.error("Error al regenerar la llave");
    }
  };

  const guardarIntervaloAgente = async (v) => {
    try {
      const res = await axios.put(`${API}/camaras/agente/config`, { snapshot_interval_sec: parseInt(v, 10) || 15 }, getAuthHeaders());
      setAgenteCfg((c) => ({ ...c, snapshot_interval_sec: res.data.snapshot_interval_sec }));
      toast.success("Intervalo del agente guardado");
    } catch {
      toast.error("Error al guardar el intervalo");
    }
  };

  const guardarDetCfg = async () => {
    setGuardandoDetCfg(true);
    try {
      await axios.put(`${API}/camaras/detecciones/config`, {
        ...detCfg, intervalo_min: parseInt(detCfg.intervalo_min, 10) || 2
      }, getAuthHeaders());
      toast.success("Configuración de detección guardada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setGuardandoDetCfg(false);
    }
  };

  const guardarPersona = async () => {
    if (!personaForm.nombre || !personaForm.foto) {
      toast.error("Indique el nombre y la foto de la persona");
      return;
    }
    setGuardandoPersona(true);
    try {
      const fd = new FormData();
      fd.append("nombre", personaForm.nombre);
      fd.append("notas", personaForm.notas || "");
      fd.append("foto", personaForm.foto);
      await axios.post(`${API}/camaras/personas`, fd, getAuthHeaders());
      toast.success("Persona agregada a la galería");
      setShowPersonaDialog(false);
      setPersonaForm({ nombre: "", notas: "", foto: null });
      const r = await axios.get(`${API}/camaras/personas`, getAuthHeaders());
      setPersonas(r.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar la persona");
    } finally {
      setGuardandoPersona(false);
    }
  };

  const eliminarPersona = async (p) => {
    if (!window.confirm(`¿Eliminar a "${p.nombre}" de la galería?`)) return;
    try {
      await axios.delete(`${API}/camaras/personas/${p.id}`, getAuthHeaders());
      setPersonas((arr) => arr.filter((x) => x.id !== p.id));
      toast.success("Persona eliminada");
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const verFotoDeteccion = async (det) => {
    if (!det.captura_id) return;
    setFotoDet({ det, url: null });
    try {
      const res = await axios.get(`${API}/camaras/capturas/${det.captura_id}`, { ...getAuthHeaders(), responseType: "blob" });
      setFotoDet({ det, url: URL.createObjectURL(res.data) });
    } catch {
      toast.error("No se pudo cargar la captura");
      setFotoDet(null);
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="camaras-page">
        <div className="flex flex-wrap justify-between gap-3 mb-6">
          <div>
            <h1 className="text-3xl font-bold dark:text-white flex items-center gap-2">
              <Cctv className="w-7 h-7 text-[#0C314C]" /> Cámaras de Seguridad
            </h1>
            <p className="text-slate-500">Vistas en vivo, análisis con IA y registro de matrículas</p>
          </div>
        </div>

        <Tabs defaultValue="vistas">
          <TabsList data-testid="camaras-tabs">
            <TabsTrigger value="vistas" data-testid="tab-vistas"><Camera className="w-4 h-4 mr-1" />Vistas</TabsTrigger>
            <TabsTrigger value="matriculas" data-testid="tab-matriculas"><ScanLine className="w-4 h-4 mr-1" />Matrículas</TabsTrigger>
            <TabsTrigger value="detecciones" data-testid="tab-detecciones"><UserSearch className="w-4 h-4 mr-1" />Personas</TabsTrigger>
            {isAdmin && <TabsTrigger value="config" data-testid="tab-config"><Settings className="w-4 h-4 mr-1" />Configuración</TabsTrigger>}
          </TabsList>

          <TabsContent value="vistas" className="mt-4">
            {sesionExpirada && (
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-800" data-testid="camaras-sesion-expirada-banner">
                <div className="flex items-start gap-2 text-amber-800 dark:text-amber-300 text-sm">
                  <KeyRound className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span><strong>Su sesión expiró.</strong> Las cámaras sí están conectadas y enviando imágenes — solo debe volver a iniciar sesión para verlas.</span>
                </div>
                <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" onClick={logout} data-testid="camaras-relogin-btn">
                  Volver a iniciar sesión
                </Button>
              </div>
            )}
            {loading ? (
              <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
            ) : camarasActivas.length === 0 ? (
              <Card><CardContent className="py-16 text-center text-slate-500">
                <Cctv className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No hay cámaras configuradas todavía.</p>
                {isAdmin && <p className="text-sm mt-1">Vaya a la pestaña <strong>Configuración</strong> para agregar sus NVR.</p>}
              </CardContent></Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="camaras-grid">
                {camarasActivas.map(({ nvr, canal }) => (
                  <CamaraTile key={`${nvr.id}-${canal.numero}`} nvr={nvr} canal={canal} getAuthHeaders={getAuthHeaders} onAnalizar={handleAnalizar} onAmpliar={handleAmpliar} onAuthError={handleAuthError} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="matriculas" className="mt-4 space-y-4">
            {isAdmin && (
              <Card>
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-end gap-4">
                    <div className="flex items-center gap-2 pb-2">
                      <Switch checked={!!matCfg.activo} onCheckedChange={(v) => setMatCfg({ ...matCfg, activo: v })} data-testid="matriculas-activo" />
                      <Label>Escaneo automático activo</Label>
                    </div>
                    <div>
                      <Label className="text-xs">Cada (minutos)</Label>
                      <Input type="number" min={1} className="w-24" value={matCfg.intervalo_min} onChange={(e) => setMatCfg({ ...matCfg, intervalo_min: e.target.value })} data-testid="matriculas-intervalo" />
                    </div>
                    <div>
                      <Label className="text-xs">Desde (opcional)</Label>
                      <Input type="time" className="w-28" value={matCfg.hora_inicio || ""} onChange={(e) => setMatCfg({ ...matCfg, hora_inicio: e.target.value })} data-testid="matriculas-hora-inicio" />
                    </div>
                    <div>
                      <Label className="text-xs">Hasta (opcional)</Label>
                      <Input type="time" className="w-28" value={matCfg.hora_fin || ""} onChange={(e) => setMatCfg({ ...matCfg, hora_fin: e.target.value })} data-testid="matriculas-hora-fin" />
                    </div>
                    <Button onClick={guardarMatCfg} disabled={guardandoCfg} className="bg-[#0C314C] hover:bg-[#16304f] text-white" data-testid="matriculas-guardar-cfg">
                      {guardandoCfg && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Guardar
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">Escanea solo las cámaras marcadas como <strong>Entrada/Salida</strong> en Configuración. Sin horario = 24/7.</p>
                </CardContent>
              </Card>
            )}

            <div className="flex items-center gap-2">
              <div className="relative flex-1 max-w-sm">
                <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                <Input className="pl-9" placeholder="Buscar placa, vehículo o cámara..." value={buscarMat} onChange={(e) => setBuscarMat(e.target.value)} data-testid="matriculas-buscar" />
              </div>
              <Badge variant="outline" data-testid="matriculas-total">{matriculas.length} registros</Badge>
              {isAdmin && (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-600 border-red-300 hover:bg-red-50"
                  data-testid="matriculas-limpiar-invalidas"
                  onClick={async () => {
                    if (!window.confirm("¿Eliminar del historial todas las matrículas con formato que no corresponde a placas de RD (registros antiguos erróneos)?")) return;
                    try {
                      const r = await axios.post(`${API}/camaras/matriculas/limpiar-invalidas`, {}, getAuthHeaders());
                      toast.success(`${r.data.eliminadas} matrícula(s) inválida(s) eliminada(s)`);
                      fetchMatriculas(buscarMat);
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Error limpiando matrículas");
                    }
                  }}
                >
                  Limpiar placas inválidas
                </Button>
              )}
            </div>

            <Card>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 dark:bg-slate-800 text-left">
                    <tr>
                      <th className="p-3">Matrícula</th>
                      <th className="p-3">Placa (recorte)</th>
                      <th className="p-3">Vehículo</th>
                      <th className="p-3">Cámara</th>
                      <th className="p-3">Fecha / Hora</th>
                      <th className="p-3">Foto</th>
                    </tr>
                  </thead>
                  <tbody data-testid="matriculas-tabla">
                    {matriculas.length === 0 && (
                      <tr><td colSpan={6} className="p-8 text-center text-slate-400">No hay matrículas registradas todavía</td></tr>
                    )}
                    {matriculas.map((m) => (
                      <tr key={m.id} className="border-t dark:border-slate-700">
                        <td className="p-3 font-mono font-bold dark:text-white">{m.matricula}</td>
                        <td className="p-3">
                          {recortes[m.id] ? (
                            <img
                              src={recortes[m.id]}
                              alt={`placa ${m.matricula}`}
                              className="h-10 rounded border cursor-pointer hover:scale-150 transition-transform origin-left"
                              onClick={() => handleVerFoto(m)}
                              data-testid={`matricula-recorte-${m.id}`}
                            />
                          ) : <span className="text-slate-400 text-xs">-</span>}
                        </td>
                        <td className="p-3">{m.vehiculo || "-"}</td>
                        <td className="p-3">{m.camara_nombre} <span className="text-xs text-slate-400">({m.nvr_nombre})</span></td>
                        <td className="p-3">{fmtFecha(m.fecha)}</td>
                        <td className="p-3">
                          {m.captura_id ? (
                            <Button variant="outline" size="sm" onClick={() => handleVerFoto(m)} data-testid={`matricula-foto-${m.id}`}>
                              <Camera className="w-4 h-4" />
                            </Button>
                          ) : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="detecciones" className="mt-4 space-y-4">
            {isAdmin && (
              <Card>
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-end gap-4">
                    <div className="flex items-center gap-2 pb-2">
                      <Switch checked={!!detCfg.activo} onCheckedChange={(v) => setDetCfg({ ...detCfg, activo: v })} data-testid="detecciones-activo" />
                      <Label>Detección de personas activa</Label>
                    </div>
                    <div>
                      <Label className="text-xs">Cada (minutos)</Label>
                      <Input type="number" min={1} className="w-24" value={detCfg.intervalo_min} onChange={(e) => setDetCfg({ ...detCfg, intervalo_min: e.target.value })} data-testid="detecciones-intervalo" />
                    </div>
                    <div>
                      <Label className="text-xs">Desde (opcional)</Label>
                      <Input type="time" className="w-28" value={detCfg.hora_inicio || ""} onChange={(e) => setDetCfg({ ...detCfg, hora_inicio: e.target.value })} data-testid="detecciones-hora-inicio" />
                    </div>
                    <div>
                      <Label className="text-xs">Hasta (opcional)</Label>
                      <Input type="time" className="w-28" value={detCfg.hora_fin || ""} onChange={(e) => setDetCfg({ ...detCfg, hora_fin: e.target.value })} data-testid="detecciones-hora-fin" />
                    </div>
                    <Button onClick={guardarDetCfg} disabled={guardandoDetCfg} className="bg-[#0C314C] hover:bg-[#16304f] text-white" data-testid="detecciones-guardar-cfg">
                      {guardandoDetCfg && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Guardar
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">Escanea las cámaras marcadas con <strong>Personas</strong> en Configuración. Ej.: active solo fuera de horario laboral (18:00 a 07:00). Sin horario = 24/7.</p>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="flex items-center gap-2"><UserSearch className="w-4 h-4" />Personas conocidas (galería)</Label>
                  {isAdmin && (
                    <Button variant="outline" size="sm" onClick={() => setShowPersonaDialog(true)} data-testid="persona-agregar-btn">
                      <Plus className="w-4 h-4 mr-1" />Agregar persona
                    </Button>
                  )}
                </div>
                {personas.length === 0 ? (
                  <p className="text-sm text-slate-400">Sin personas registradas. La IA marcará todas las detecciones como desconocidas.</p>
                ) : (
                  <div className="flex flex-wrap gap-3" data-testid="personas-galeria">
                    {personas.map((p) => (
                      <div key={p.id} className="border rounded-lg p-2 w-32 text-center bg-slate-50 dark:bg-slate-800/50" data-testid={`persona-${p.id}`}>
                        <PersonaFoto personaId={p.id} nombre={p.nombre} getAuthHeaders={getAuthHeaders} />
                        <p className="text-xs font-medium truncate dark:text-white">{p.nombre}</p>
                        {isAdmin && (
                          <Button variant="ghost" size="sm" className="text-red-500 h-6 px-2 mt-1" onClick={() => eliminarPersona(p)} data-testid={`persona-eliminar-${p.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-slate-400 mt-2">El reconocimiento es orientativo (comparación visual con IA), no es biometría certificada.</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 dark:bg-slate-800 text-left">
                    <tr>
                      <th className="p-3">Fecha / Hora</th>
                      <th className="p-3">Cámara</th>
                      <th className="p-3">Personas</th>
                      <th className="p-3">Conocidas</th>
                      <th className="p-3">Descripción</th>
                      <th className="p-3">Foto</th>
                    </tr>
                  </thead>
                  <tbody data-testid="detecciones-tabla">
                    {detecciones.length === 0 && (
                      <tr><td colSpan={6} className="p-8 text-center text-slate-400">No hay detecciones registradas todavía</td></tr>
                    )}
                    {detecciones.map((d) => (
                      <tr key={d.id} className="border-t dark:border-slate-700">
                        <td className="p-3 whitespace-nowrap">{fmtFecha(d.fecha)}</td>
                        <td className="p-3">{d.camara_nombre} <span className="text-xs text-slate-400">({d.nvr_nombre})</span></td>
                        <td className="p-3"><Badge variant="outline"><Users className="w-3 h-3 mr-1" />{d.personas}</Badge></td>
                        <td className="p-3">
                          {(d.coincidencias || []).length > 0
                            ? (d.coincidencias || []).map((c, i) => <Badge key={i} className="bg-emerald-600 mr-1">{c.nombre}</Badge>)
                            : <Badge variant="outline" className="text-amber-600 border-amber-300">{d.desconocidos ?? d.personas} desconocida(s)</Badge>}
                        </td>
                        <td className="p-3 max-w-xs truncate" title={d.descripcion}>{d.descripcion || "-"}</td>
                        <td className="p-3">
                          {d.captura_id ? (
                            <Button variant="outline" size="sm" onClick={() => verFotoDeteccion(d)} data-testid={`deteccion-foto-${d.id}`}>
                              <Camera className="w-4 h-4" />
                            </Button>
                          ) : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>

          {isAdmin && (
            <TabsContent value="config" className="mt-4 space-y-4">
              <Card className="border-emerald-200 dark:border-emerald-800">
                <CardContent className="p-4 space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <Label className="flex items-center gap-2"><MonitorSmartphone className="w-4 h-4 text-emerald-600" />Agente de Conexión Local</Label>
                      <p className="text-xs text-slate-500 mt-1">Instálelo en una PC de la oficina (misma red que los NVR). Sube las cámaras a la nube sin abrir puertos en el router — igual que el agente de Ponche.</p>
                    </div>
                    <Button onClick={descargarAgente} className="bg-emerald-600 hover:bg-emerald-700" data-testid="agente-descargar-btn">
                      <Download className="w-4 h-4 mr-1" />Descargar Agente (ZIP)
                    </Button>
                  </div>
                  <div className="flex flex-wrap items-end gap-4">
                    <div>
                      <Label className="text-xs">Estado</Label>
                      <div className="text-sm space-y-0.5" data-testid="agente-estado">
                        {agenteCfg?.last_seen_at
                          ? <p className="text-emerald-600">● Agente en línea — últ. contacto: {fmtFecha(agenteCfg.last_seen_at)}</p>
                          : <p className="text-slate-400">○ El agente nunca ha contactado la plataforma</p>}
                        {agenteCfg?.last_push_at
                          ? <p className="text-emerald-600 text-xs">📷 Última imagen recibida: {fmtFecha(agenteCfg.last_push_at)}</p>
                          : <p className="text-amber-600 text-xs">⚠ Sin imágenes aún — si el agente está en línea, revise IP/puerto/credenciales del NVR (vea el error en la tarjeta del NVR)</p>}
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Sube imágenes cada (seg)</Label>
                      <Input type="number" min={5} max={300} className="w-24" defaultValue={agenteCfg?.snapshot_interval_sec ?? 15}
                        onBlur={(e) => guardarIntervaloAgente(e.target.value)} data-testid="agente-intervalo" />
                    </div>
                    <div className="flex-1 min-w-[220px]">
                      <Label className="text-xs">Llave API del agente</Label>
                      <div className="flex gap-2">
                        <Input readOnly type={mostrarKey ? "text" : "password"} value={agenteCfg?.api_key || ""} className="font-mono text-xs" data-testid="agente-api-key" />
                        <Button variant="outline" size="sm" onClick={() => setMostrarKey(!mostrarKey)}>{mostrarKey ? "Ocultar" : "Ver"}</Button>
                        <Button variant="outline" size="sm" onClick={regenerarKey} data-testid="agente-regenerar-key"><KeyRound className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-end">
                <Button onClick={() => { setNvrForm({ ...NVR_VACIO, canales: [] }); setShowNvrDialog(true); }} className="bg-[#0C314C] hover:bg-[#16304f] text-white" data-testid="nvr-nuevo-btn">
                  <Plus className="w-4 h-4 mr-1" />Agregar NVR
                </Button>
              </div>
              {nvrs.length === 0 && (
                <Card><CardContent className="py-10 text-center text-slate-500">
                  <Wifi className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p>Agregue su primer NVR con su IP/DDNS pública y el puerto HTTP redirigido en el router.</p>
                </CardContent></Card>
              )}
              {nvrs.map((nvr) => (
                <Card key={nvr.id} data-testid={`nvr-card-${nvr.id}`}>
                  <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold dark:text-white">{nvr.nombre} {nvr.modo === "agente" && <Badge variant="outline" className="ml-1 text-xs">Agente local</Badge>}</p>
                      <p className="text-sm text-slate-500">{nvr.host}:{nvr.puerto} · {(nvr.canales || []).length} cámaras · usuario {nvr.usuario}</p>
                      {nvr.ultima_sync && <p className="text-xs text-emerald-600">Última imagen: {fmtFecha(nvr.ultima_sync)}</p>}
                      {nvr.ultimo_error && (
                        <p className="text-xs text-red-600 mt-1 max-w-xl" data-testid={`nvr-error-${nvr.id}`}>
                          <AlertTriangle className="w-3 h-3 inline mr-1" />Último error del agente: {nvr.ultimo_error}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => { setNvrForm({ ...nvr, password: "" }); setShowNvrDialog(true); }} data-testid={`nvr-editar-${nvr.id}`}>
                        <Settings className="w-4 h-4 mr-1" />Editar
                      </Button>
                      <Button variant="outline" size="sm" className="text-red-600" onClick={() => eliminarNvr(nvr)} data-testid={`nvr-eliminar-${nvr.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </TabsContent>
          )}
        </Tabs>

        {/* Dialog NVR */}
        <Dialog open={showNvrDialog} onOpenChange={setShowNvrDialog}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{nvrForm.id ? "Editar NVR" : "Agregar NVR"}</DialogTitle>
              <DialogDescription>NVR ANNKE / compatible Hikvision.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Modo de conexión</Label>
                <Select value={nvrForm.modo || "agente"} onValueChange={(v) => setNvrForm({ ...nvrForm, modo: v })}>
                  <SelectTrigger data-testid="nvr-form-modo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="agente">Agente local (recomendado — sin abrir puertos)</SelectItem>
                    <SelectItem value="directa">Directa (IP pública/DDNS con puertos abiertos)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  {(nvrForm.modo || "agente") === "agente"
                    ? "Use la IP LOCAL del NVR (ej. 192.168.1.50). El Agente instalado en una PC de la oficina hará el enlace."
                    : "Use su IP pública o DDNS con el puerto HTTP redirigido en el router."}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Nombre</Label>
                  <Input placeholder="NVR Oficina Principal" value={nvrForm.nombre} onChange={(e) => setNvrForm({ ...nvrForm, nombre: e.target.value })} data-testid="nvr-form-nombre" />
                </div>
                <div>
                  <Label>{(nvrForm.modo || "agente") === "agente" ? "IP local del NVR" : "Host (IP pública o DDNS)"}</Label>
                  <Input placeholder={(nvrForm.modo || "agente") === "agente" ? "192.168.1.50" : "miempresa.ddns.net"} value={nvrForm.host} onChange={(e) => setNvrForm({ ...nvrForm, host: e.target.value })} data-testid="nvr-form-host" />
                </div>
                <div>
                  <Label>Puerto HTTP</Label>
                  <Input type="number" value={nvrForm.puerto} onChange={(e) => setNvrForm({ ...nvrForm, puerto: e.target.value })} data-testid="nvr-form-puerto" />
                </div>
                <div>
                  <Label>Usuario</Label>
                  <Input placeholder="admin" value={nvrForm.usuario} onChange={(e) => setNvrForm({ ...nvrForm, usuario: e.target.value })} data-testid="nvr-form-usuario" />
                </div>
                <div className="col-span-2">
                  <Label>Contraseña {nvrForm.id && <span className="text-xs text-slate-400">(deje vacío para no cambiarla)</span>}</Label>
                  <Input type="password" value={nvrForm.password || ""} onChange={(e) => setNvrForm({ ...nvrForm, password: e.target.value })} data-testid="nvr-form-password" />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <Label>Cámaras (canales)</Label>
                <Button variant="outline" size="sm" onClick={() => setNvrForm((f) => ({ ...f, canales: [...f.canales, { numero: f.canales.length + 1, nombre: "", activo: true, es_entrada: false }] }))} data-testid="nvr-agregar-canal">
                  <Plus className="w-4 h-4 mr-1" />Agregar cámara
                </Button>
              </div>
              {nvrForm.canales.map((c, idx) => (
                <div key={idx} className="flex items-center gap-2 border rounded-lg p-2 bg-slate-50 dark:bg-slate-800/50">
                  <Input type="number" min={1} className="w-16 shrink-0" value={c.numero} onChange={(e) => setCanal(idx, { numero: e.target.value })} data-testid={`canal-numero-${idx}`} title="Nº de canal en el NVR" />
                  <Input placeholder="Nombre (ej. Parqueo, Entrada)" value={c.nombre} onChange={(e) => setCanal(idx, { nombre: e.target.value })} data-testid={`canal-nombre-${idx}`} />
                  <div className="flex items-center gap-1 shrink-0" title="Activa">
                    <Switch checked={c.activo !== false} onCheckedChange={(v) => setCanal(idx, { activo: v })} data-testid={`canal-activo-${idx}`} />
                    <span className="text-xs text-slate-500">Activa</span>
                  </div>
                  <div className="flex items-center gap-1 shrink-0" title="Cámara de entrada/salida para registro de matrículas">
                    <Switch checked={!!c.es_entrada} onCheckedChange={(v) => setCanal(idx, { es_entrada: v })} data-testid={`canal-entrada-${idx}`} />
                    <span className="text-xs text-slate-500">Entrada/Salida</span>
                  </div>
                  <div className="flex items-center gap-1 shrink-0" title="Detección de personas/rostros en esta cámara">
                    <Switch checked={!!c.detectar_personas} onCheckedChange={(v) => setCanal(idx, { detectar_personas: v })} data-testid={`canal-personas-${idx}`} />
                    <span className="text-xs text-slate-500">Personas</span>
                  </div>
                  <Button variant="ghost" size="sm" className="text-red-500 shrink-0 px-2" onClick={() => setNvrForm((f) => ({ ...f, canales: f.canales.filter((_, i) => i !== idx) }))} data-testid={`canal-quitar-${idx}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
            <DialogFooter className="gap-2">
              {nvrForm.id && (
                <Button variant="outline" onClick={probarNvr} disabled={probando} data-testid="nvr-probar-btn">
                  {probando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wifi className="w-4 h-4 mr-1" />}Probar conexión
                </Button>
              )}
              <Button variant="outline" onClick={() => setShowNvrDialog(false)}>Cancelar</Button>
              <Button onClick={guardarNvr} disabled={guardandoNvr} className="bg-[#0C314C] hover:bg-[#16304f] text-white" data-testid="nvr-guardar-btn">
                {guardandoNvr && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Guardar NVR
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Análisis IA */}
        <Dialog open={!!analisis} onOpenChange={(v) => !v && setAnalisis(null)}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-violet-600" />Análisis IA — {analisis?.camara}</DialogTitle>
            </DialogHeader>
            {analisis?.cargando ? (
              <div className="py-12 text-center">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-violet-500 mb-3" />
                <p className="text-slate-500">{analisis?.preguntaEnviada ? "Analizando y respondiendo tu pregunta..." : "Capturando imagen y analizando con IA..."}</p>
              </div>
            ) : analisis && (
              <div className="space-y-3" data-testid="analisis-resultado">
                {analisis.imagen_b64 && <img src={`data:image/jpeg;base64,${analisis.imagen_b64}`} alt="captura" className="w-full rounded-lg border" />}

                {analisis.resultado?.respuesta_pregunta && (
                  <div className="border-l-4 border-violet-500 bg-violet-50 dark:bg-violet-950/30 rounded-r-lg p-3" data-testid="analisis-respuesta-pregunta">
                    <p className="text-xs font-semibold text-violet-600 mb-1">Tu pregunta: {analisis.preguntaEnviada}</p>
                    <p className="text-sm dark:text-slate-200">{analisis.resultado.respuesta_pregunta}</p>
                  </div>
                )}

                {analisis.resultado?.resumen && <p className="text-sm font-medium dark:text-slate-200">{analisis.resultado.resumen}</p>}

                {analisis.resultado?.alerta && (
                  <div className="border-l-4 border-red-500 bg-red-50 dark:bg-red-950/30 rounded-r-lg p-3">
                    <p className="text-sm text-red-700 dark:text-red-400 flex items-start gap-1"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />{analisis.resultado.alerta}</p>
                  </div>
                )}

                <div className="border rounded-lg p-3">
                  <p className="text-xs font-semibold text-slate-500 uppercase mb-1 flex items-center gap-1"><Users className="w-3.5 h-3.5" />Personas ({analisis.resultado?.personas?.cantidad ?? 0})</p>
                  {(analisis.resultado?.personas?.detalle || []).length > 0 ? (
                    <ul className="text-sm space-y-1 dark:text-slate-300">
                      {(analisis.resultado.personas.detalle || []).map((p, i) => <li key={i} className="pl-2 border-l-2 border-slate-200 dark:border-slate-700">{p}</li>)}
                    </ul>
                  ) : <p className="text-sm text-slate-400">No se detectan personas</p>}
                </div>

                <div className="border rounded-lg p-3">
                  <p className="text-xs font-semibold text-slate-500 uppercase mb-1 flex items-center gap-1"><Car className="w-3.5 h-3.5" />Vehículos ({(analisis.resultado?.vehiculos || []).length})</p>
                  {(analisis.resultado?.vehiculos || []).length > 0 ? (
                    <ul className="text-sm space-y-1 dark:text-slate-300">
                      {(analisis.resultado.vehiculos || []).map((v, i) => (
                        <li key={i} className="pl-2 border-l-2 border-slate-200 dark:border-slate-700">
                          {[v.tipo, v.color, v.marca_modelo].filter(Boolean).join(" · ")}
                          {v.placa && <Badge className="bg-amber-500 ml-2 text-xs"><ScanLine className="w-3 h-3 mr-1" />{v.placa}</Badge>}
                          {v.estado && <span className="text-xs text-slate-400 ml-2">({v.estado})</span>}
                        </li>
                      ))}
                    </ul>
                  ) : <p className="text-sm text-slate-400">No se detectan vehículos</p>}
                </div>

                <div className="border rounded-lg p-3">
                  <p className="text-xs font-semibold text-slate-500 uppercase mb-1 flex items-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5" />Seguridad
                    {analisis.resultado?.seguridad?.nivel && (
                      <Badge className={`ml-1 text-xs ${analisis.resultado.seguridad.nivel === "alerta" ? "bg-red-600" : analisis.resultado.seguridad.nivel === "atencion" ? "bg-amber-500" : "bg-emerald-600"}`}>
                        {analisis.resultado.seguridad.nivel}
                      </Badge>
                    )}
                  </p>
                  {(analisis.resultado?.seguridad?.observaciones || []).length > 0 ? (
                    <ul className="text-sm space-y-1 dark:text-slate-300">
                      {(analisis.resultado.seguridad.observaciones || []).map((o, i) => <li key={i} className="pl-2 border-l-2 border-amber-300">{o}</li>)}
                    </ul>
                  ) : <p className="text-sm text-slate-400">Sin observaciones de seguridad</p>}
                </div>

                {analisis.resultado?.entorno && (
                  <div className="border rounded-lg p-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm dark:text-slate-300">
                    <div><p className="text-xs font-semibold text-slate-500 uppercase">Iluminación</p><p>{analisis.resultado.entorno.iluminacion || "-"}</p></div>
                    <div><p className="text-xs font-semibold text-slate-500 uppercase">Clima</p><p>{analisis.resultado.entorno.clima || "-"}</p></div>
                    <div><p className="text-xs font-semibold text-slate-500 uppercase">Visibilidad cámara</p><p>{analisis.resultado.entorno.visibilidad_camara || "-"}</p></div>
                  </div>
                )}

                <div className="flex gap-2 pt-1">
                  <Input placeholder='Pregunta específica, ej. "¿hay alguien cerca del portón?"' value={preguntaIA} onChange={(e) => setPreguntaIA(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && preguntaIA.trim()) { handleAnalizar(analisis.nvr, analisis.canal, preguntaIA.trim()); setPreguntaIA(""); } }}
                    data-testid="analisis-pregunta-input" />
                  <Button className="bg-violet-600 hover:bg-violet-700 text-white shrink-0" disabled={!preguntaIA.trim()}
                    onClick={() => { handleAnalizar(analisis.nvr, analisis.canal, preguntaIA.trim()); setPreguntaIA(""); }} data-testid="analisis-preguntar-btn">
                    <Sparkles className="w-4 h-4 mr-1" />Preguntar
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Dialog cámara ampliada */}
        <Dialog open={!!ampliada} onOpenChange={(v) => !v && setAmpliada(null)}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>{ampliada?.canal?.nombre} <span className="text-sm text-slate-400 font-normal">({ampliada?.nvr?.nombre})</span></DialogTitle>
            </DialogHeader>
            {ampliada?.url ? (
              <img src={ampliada.url} alt="vista ampliada" className="w-full rounded-lg" data-testid="camara-ampliada-img" />
            ) : (
              <div className="py-16 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" /></div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => ampliada && handleAmpliar(ampliada.nvr, ampliada.canal)}><RefreshCw className="w-4 h-4 mr-1" />Refrescar</Button>
              <Button className="bg-violet-600 hover:bg-violet-700" onClick={() => { const a = ampliada; setAmpliada(null); handleAnalizar(a.nvr, a.canal); }}>
                <Sparkles className="w-4 h-4 mr-1" />Analizar con IA
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog foto matrícula */}
        <Dialog open={!!fotoMat} onOpenChange={(v) => !v && setFotoMat(null)}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Matrícula {fotoMat?.registro?.matricula} — {fotoMat?.registro?.camara_nombre}</DialogTitle>
              <DialogDescription>{fmtFecha(fotoMat?.registro?.fecha)}</DialogDescription>
            </DialogHeader>
            {fotoMat?.recorteUrl && (
              <div className="text-center">
                <p className="text-xs text-slate-500 mb-1">Recorte ampliado de la placa</p>
                <img src={fotoMat.recorteUrl} alt="recorte placa" className="inline-block max-h-40 rounded-lg border" data-testid="matricula-recorte-ampliado" />
              </div>
            )}
            {fotoMat?.url ? (
              <img src={fotoMat.url} alt="captura matrícula" className="w-full rounded-lg" />
            ) : (
              <div className="py-16 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" /></div>
            )}
          </DialogContent>
        </Dialog>
        {/* Dialog agregar persona */}
        <Dialog open={showPersonaDialog} onOpenChange={setShowPersonaDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Agregar persona conocida</DialogTitle>
              <DialogDescription>Suba una foto clara del rostro para que la IA pueda compararla en las detecciones.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Nombre</Label>
                <Input value={personaForm.nombre} onChange={(e) => setPersonaForm({ ...personaForm, nombre: e.target.value })} data-testid="persona-form-nombre" />
              </div>
              <div>
                <Label>Notas (opcional)</Label>
                <Input placeholder="Empleado, visitante frecuente..." value={personaForm.notas} onChange={(e) => setPersonaForm({ ...personaForm, notas: e.target.value })} data-testid="persona-form-notas" />
              </div>
              <div>
                <Label>Foto</Label>
                <Input type="file" accept="image/*" onChange={(e) => setPersonaForm({ ...personaForm, foto: e.target.files?.[0] || null })} data-testid="persona-form-foto" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPersonaDialog(false)}>Cancelar</Button>
              <Button onClick={guardarPersona} disabled={guardandoPersona} className="bg-[#0C314C] hover:bg-[#16304f] text-white" data-testid="persona-guardar-btn">
                {guardandoPersona && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog foto detección */}
        <Dialog open={!!fotoDet} onOpenChange={(v) => !v && setFotoDet(null)}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Detección en {fotoDet?.det?.camara_nombre}</DialogTitle>
              <DialogDescription>{fmtFecha(fotoDet?.det?.fecha)} — {fotoDet?.det?.descripcion}</DialogDescription>
            </DialogHeader>
            {fotoDet?.url ? (
              <img src={fotoDet.url} alt="captura detección" className="w-full rounded-lg" />
            ) : (
              <div className="py-16 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" /></div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
